"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeStepStates = exports.resolveStageOrdinal = exports.getOrderedStepsForRefiType = exports.RefiStepState = exports.FIRST_LIEN_HEI_REFI_STATUS_ARRAY = exports.FIRST_LIEN_REFI_STATUS_ARRAY = exports.HELOC_REFI_STATUS_ARRAY = void 0;
const lienRefi_1 = require("./lienRefi");
/**
 * Canonical ordering + state logic for the lien-refi status timeline.
 *
 * This is the single source of truth shared by:
 *  - the borrower-facing Vue UI (`aven_shared` re-exports the arrays from here),
 *  - the backend (`helocRefiManager.getPriorLienReleaseDetail`), which computes the ordered
 *    steps server-side so the partner dashboard React app stays free of any Vue coupling.
 *
 * The arrays and the `getOrderedStepsForRefiType` / `computeStepStates` helpers mirror the
 * logic in `aven_my`'s `RefiStatusCard.vue` (the most complete variant, incl. auth-status
 * substitution) so every surface renders the same steps in the same order.
 */
/**
 * Status progression array for HELOC refi (second lien).
 * Order: SEND_CARD → UPLOAD_STATEMENT → PREPARING_BORROWER_AUTH → REQUEST_PAYOFF_QUOTE → SEND_PAYOFF → COMPLETE_PAYOFF → ACTIVATE_ACCOUNT
 */
exports.HELOC_REFI_STATUS_ARRAY = [
    lienRefi_1.RefiStageForFrontend.SEND_CARD,
    lienRefi_1.RefiStageForFrontend.UPLOAD_STATEMENT,
    lienRefi_1.RefiStageForFrontend.PREPARING_BORROWER_AUTH,
    lienRefi_1.RefiStageForFrontend.REQUEST_PAYOFF_QUOTE,
    lienRefi_1.RefiStageForFrontend.SEND_PAYOFF,
    lienRefi_1.RefiStageForFrontend.COMPLETE_PAYOFF,
    lienRefi_1.RefiStageForFrontend.ACTIVATE_ACCOUNT,
];
/**
 * Status progression array for First Lien.
 * Order: SEND_CARD → UPLOAD_STATEMENT → TRANSFER_FUNDS → PREPARING_BORROWER_AUTH → REQUEST_PAYOFF_QUOTE → ACTIVATE_ACCOUNT
 * Note: The final step (ACTIVATE_ACCOUNT) displays as "Refinance complete!" for first lien.
 */
exports.FIRST_LIEN_REFI_STATUS_ARRAY = [
    lienRefi_1.RefiStageForFrontend.SEND_CARD,
    lienRefi_1.RefiStageForFrontend.UPLOAD_STATEMENT,
    lienRefi_1.RefiStageForFrontend.TRANSFER_FUNDS,
    lienRefi_1.RefiStageForFrontend.PREPARING_BORROWER_AUTH,
    lienRefi_1.RefiStageForFrontend.REQUEST_PAYOFF_QUOTE,
    lienRefi_1.RefiStageForFrontend.ACTIVATE_ACCOUNT,
];
/**
 * Status progression array for First Lien HEI (Home Equity Investment) lender refi.
 * Order: SEND_CARD → UPLOAD_STATEMENT → PREPARING_BORROWER_AUTH → REQUEST_PAYOFF_QUOTE → TRANSFER_FUNDS → ACTIVATE_ACCOUNT
 * Note: TRANSFER_FUNDS occurs after REQUEST_PAYOFF_QUOTE for HEI lenders, unlike standard first lien refis.
 */
exports.FIRST_LIEN_HEI_REFI_STATUS_ARRAY = [
    lienRefi_1.RefiStageForFrontend.SEND_CARD,
    lienRefi_1.RefiStageForFrontend.UPLOAD_STATEMENT,
    lienRefi_1.RefiStageForFrontend.PREPARING_BORROWER_AUTH,
    lienRefi_1.RefiStageForFrontend.REQUEST_PAYOFF_QUOTE,
    lienRefi_1.RefiStageForFrontend.TRANSFER_FUNDS,
    lienRefi_1.RefiStageForFrontend.ACTIVATE_ACCOUNT,
];
/** Rendered state of a single step in the refi timeline. */
var RefiStepState;
(function (RefiStepState) {
    RefiStepState["completed"] = "completed";
    RefiStepState["current"] = "current";
    RefiStepState["upcoming"] = "upcoming";
})(RefiStepState || (exports.RefiStepState = RefiStepState = {}));
/**
 * Selects the base ordered step array for a refi, by type. First-lien and multi-lien refis share
 * the first-lien ordering (with an HEI-specific variant when the lender is a Home Equity Investment
 * lender); everything else uses the HELOC ordering. Mirrors `RefiStatusCard.vue`'s array selection.
 */
const getOrderedStepsForRefiType = ({ refiType, isHei }) => {
    const isFirstLienOrMultiLien = refiType === lienRefi_1.RefiType.FIRST || refiType === lienRefi_1.RefiType.MULTI;
    if (isFirstLienOrMultiLien) {
        return isHei ? exports.FIRST_LIEN_HEI_REFI_STATUS_ARRAY : exports.FIRST_LIEN_REFI_STATUS_ARRAY;
    }
    return exports.HELOC_REFI_STATUS_ARRAY;
};
exports.getOrderedStepsForRefiType = getOrderedStepsForRefiType;
const BORROWER_AUTH_STATUSES = [lienRefi_1.RefiStageForFrontend.REQUIRE_BORROWER_AUTH_SIGNED_DOC, lienRefi_1.RefiStageForFrontend.REQUIRE_BORROWER_AUTH_WET_SIGNED_DOC];
/**
 * Maps a (possibly dynamic) live stage onto the base array slot it occupies: the auth statuses
 * collapse onto `PREPARING_BORROWER_AUTH` and `REVIEW_STATEMENT` collapses onto `TRANSFER_FUNDS`,
 * matching the substitution `computeStepStates` performs. Everything else maps to itself.
 */
const toBaseStage = (stage) => {
    if (BORROWER_AUTH_STATUSES.includes(stage)) {
        return lienRefi_1.RefiStageForFrontend.PREPARING_BORROWER_AUTH;
    }
    if (stage === lienRefi_1.RefiStageForFrontend.REVIEW_STATEMENT) {
        return lienRefi_1.RefiStageForFrontend.TRANSFER_FUNDS;
    }
    return stage;
};
/**
 * The chronological ordinal of a stage within its refi's progression array — i.e. how far along the
 * timeline it is. Use this to compare progress between stages (e.g. to find the least-advanced lien
 * in a multi-lien refi); do NOT use `RefiStageForFrontend` enum definition order, which is arbitrary
 * and differs from the per-refiType ordering. Dynamic stages are resolved to their base slot first;
 * a stage not present in the resolved array (e.g. `CANCELLED`) returns `Infinity` so it sorts last.
 */
const resolveStageOrdinal = ({ stage, refiType, isHei }) => {
    const orderedSteps = (0, exports.getOrderedStepsForRefiType)({ refiType, isHei });
    const index = orderedSteps.indexOf(toBaseStage(stage));
    return index === -1 ? Infinity : index;
};
exports.resolveStageOrdinal = resolveStageOrdinal;
const AT_OR_PAST_PAYOFF_QUOTE_STATUSES = [lienRefi_1.RefiStageForFrontend.REQUEST_PAYOFF_QUOTE, lienRefi_1.RefiStageForFrontend.SEND_PAYOFF, lienRefi_1.RefiStageForFrontend.COMPLETE_PAYOFF, lienRefi_1.RefiStageForFrontend.ACTIVATE_ACCOUNT];
/**
 * Builds the visible, ordered timeline steps for a refi at `currentStage`, tagging each step as
 * completed / current / upcoming. Mirrors `RefiStatusCard.vue` exactly:
 *  - A live `REQUIRE_BORROWER_AUTH_*` status (before payoff quote) takes the place of the
 *    `PREPARING_BORROWER_AUTH` slot; the un-current auth statuses are otherwise hidden.
 *  - A live `REVIEW_STATEMENT` status takes the place of the `TRANSFER_FUNDS` slot.
 *  - `CANCELLED` is always hidden.
 * Returns `[]` when the current stage doesn't resolve into the visible array (e.g. CANCELLED), so
 * callers render nothing rather than a misleading timeline.
 */
const computeStepStates = ({ orderedSteps, currentStage }) => {
    const isAtOrPastPayoffQuote = AT_OR_PAST_PAYOFF_QUOTE_STATUSES.includes(currentStage);
    const stagesToShow = orderedSteps
        .map((stage) => {
        // Surface the live auth status in place of the PREPARING_BORROWER_AUTH slot.
        if (stage === lienRefi_1.RefiStageForFrontend.PREPARING_BORROWER_AUTH && !isAtOrPastPayoffQuote && BORROWER_AUTH_STATUSES.includes(currentStage)) {
            return currentStage;
        }
        // Surface REVIEW_STATEMENT in place of the TRANSFER_FUNDS slot.
        if (stage === lienRefi_1.RefiStageForFrontend.TRANSFER_FUNDS && currentStage === lienRefi_1.RefiStageForFrontend.REVIEW_STATEMENT) {
            return currentStage;
        }
        return stage;
    })
        .filter((stage) => {
        if (stage === lienRefi_1.RefiStageForFrontend.CANCELLED) {
            return false;
        }
        // Auth statuses only render when they are the current status (replacing PREPARING_BORROWER_AUTH).
        if (BORROWER_AUTH_STATUSES.includes(stage) && stage !== currentStage) {
            return false;
        }
        return true;
    });
    const currentStageIndex = stagesToShow.indexOf(currentStage);
    if (currentStageIndex === -1) {
        return [];
    }
    return stagesToShow.map((stage, index) => ({
        stage,
        state: index < currentStageIndex ? RefiStepState.completed : index === currentStageIndex ? RefiStepState.current : RefiStepState.upcoming,
    }));
};
exports.computeStepStates = computeStepStates;
